import React, { useState, useMemo } from 'react';
import {
  Box,
  Checkbox,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  TextField,
  Button,
  Typography,
  Divider,
} from '@mui/material';

/**
 * Custom filter component for DataGridPro multi-select checkbox filtering
 * Displays a searchable list of checkboxes with Select All/Clear All functionality
 */
const MultiSelectColumnFilter = ({ item, applyValue, options = [] }) => {
  const [searchText, setSearchText] = useState('');

  // Get current selected values (empty array if none)
  const selectedValues = item?.value || [];

  // Filter options based on search text
  const filteredOptions = useMemo(() => {
    if (!searchText) return options;
    const lowerSearch = searchText.toLowerCase();
    return options.filter((opt) =>
      opt.label.toLowerCase().includes(lowerSearch)
    );
  }, [options, searchText]);

  // Handle individual checkbox toggle
  const handleToggle = (value) => {
    const currentIndex = selectedValues.indexOf(value);
    const newSelected = [...selectedValues];

    if (currentIndex === -1) {
      newSelected.push(value);
    } else {
      newSelected.splice(currentIndex, 1);
    }

    applyValue({
      ...item,
      value: newSelected.length > 0 ? newSelected : undefined,
    });
  };

  // Select all filtered options
  const handleSelectAll = () => {
    const allValues = filteredOptions.map((opt) => opt.value);
    const combinedValues = [...new Set([...selectedValues, ...allValues])];
    applyValue({ ...item, value: combinedValues });
  };

  // Clear all selections
  const handleClearAll = () => {
    applyValue({ ...item, value: undefined });
  };

  // Check if all filtered options are selected
  const allFilteredSelected = filteredOptions.every((opt) =>
    selectedValues.includes(opt.value)
  );

  return (
    <Box
      sx={{
        minWidth: 0,
        width: '100%',
        p: 1.5,
        overflowX: 'hidden',
      }}
    >
      {/* Search Field */}
      <TextField
        placeholder='Search...'
        size='small'
        fullWidth
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        sx={{ mb: 1 }}
      />

      {/* Select All / Clear All Buttons */}
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <Button
          size='small'
          variant='text'
          onClick={handleSelectAll}
          disabled={allFilteredSelected && filteredOptions.length > 0}
          sx={{ fontSize: '0.75rem' }}
        >
          Select All
        </Button>
        <Button
          size='small'
          variant='text'
          onClick={handleClearAll}
          disabled={selectedValues.length === 0}
          sx={{ fontSize: '0.75rem' }}
        >
          Clear All
        </Button>
      </Box>

      <Divider sx={{ mb: 1 }} />

      {/* Checkbox List with Custom Scrollbar */}
      <List
        sx={{
          maxHeight: 420,
          overflowY: 'auto',
          overflowX: 'hidden',
          width: '100%',
          p: 0,
          // Custom scrollbar styling to match MUI theme
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(0,0,0,0.1)',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(0,0,0,0.3)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(0,0,0,0.5)',
            },
          },
        }}
      >
        {filteredOptions.length === 0 ? (
          <ListItem>
            <ListItemText
              primary='No options found'
              primaryTypographyProps={{
                variant: 'body2',
                color: 'text.secondary',
              }}
            />
          </ListItem>
        ) : (
          filteredOptions.map((option) => (
            <ListItem key={option.value} disablePadding dense>
              <ListItemButton
                onClick={() => handleToggle(option.value)}
                dense
                sx={{ py: 0.5 }}
              >
                <ListItemIcon sx={{ minWidth: 36 }}>
                  <Checkbox
                    edge='start'
                    checked={selectedValues.includes(option.value)}
                    tabIndex={-1}
                    disableRipple
                    size='small'
                  />
                </ListItemIcon>
                <ListItemText
                  primary={option.label}
                  primaryTypographyProps={{
                    variant: 'body2',
                    sx: {
                      wordBreak: 'break-word',
                      overflowWrap: 'break-word',
                    },
                  }}
                />
              </ListItemButton>
            </ListItem>
          ))
        )}
      </List>

      {/* Selected Count */}
      {selectedValues.length > 0 && (
        <>
          <Divider sx={{ mt: 1, mb: 1 }} />
          <Typography variant='caption' color='text.secondary'>
            {selectedValues.length} selected
          </Typography>
        </>
      )}
    </Box>
  );
};

export default MultiSelectColumnFilter;
